import {
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '~/contexts/newToastContext';
import { getUserProfile } from '~/helpers/userProfile';
import { BigNumbers } from './BigNumbers';
import { useFilterContext } from './contexts/FilterContext';
import { useHealthDataContext } from './contexts/HealthDataContext';
import { useThermalComfortDataContext } from './contexts/ThermalComfortDataContext';
import { Health } from './Health';
import { getOverviewInfo } from './helpers/overview';
import { canViewThermalComfort } from './helpers/thermalComfort';
import { RowCardsOverview } from './RowCardsOverview';
import { ContentContainer } from './styles';
import { ThermalComfort } from './ThermalComfort';
import { OverviewInfo } from './types';

export function OverviewContent(): ReactElement {
  const {
    client, selectedOptions, viewOption, onSelectView,
  } = useFilterContext();
  const { refetch } = useHealthDataContext();
  const { refetch: refetchThermalComfort } = useThermalComfortDataContext();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<OverviewInfo | undefined>();
  const { toast } = useToast();
  const { t } = useTranslation();
  const prevClientRef = useRef<string | undefined>(undefined);
  const [profile] = useState(getUserProfile());

  async function getInfo(clientId: string): Promise<void> {
    const overviewInfo = await getOverviewInfo(Number(clientId));

    if (overviewInfo) {
      setInfo(overviewInfo);

      if (overviewInfo.hasHealth) {
        refetch(Number(client), selectedOptions);
      } else if (overviewInfo.hasThermalComfort) {
        onSelectView('thermalComfort');
      }

      if (overviewInfo.hasThermalComfort && canViewThermalComfort(profile)) {
        refetchThermalComfort(Number(client), selectedOptions);
      }
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosCliente'), 'error');
    }

    setLoading(false);
  }

  useEffect(() => {
    if (client) {
      setLoading(true);
      setInfo(undefined);
      getInfo(client);
      prevClientRef.current = client;
    }
  }, [client]);

  const clientChanged = client && prevClientRef.current !== client;
  const effectiveLoading = loading || !!clientChanged;

  const effectiveHasHealth = !!(!clientChanged && info?.hasHealth);
  const effectiveHasWater = !!(!clientChanged && info?.hasWater);
  const effectiveHasEnergy = !!(!clientChanged && info?.hasEnergy);
  const effectiveHasThermalComfort = !!(!clientChanged && info?.hasThermalComfort);

  function getOrderRowCards(): number {
    if (effectiveLoading) {
      return 2;
    }

    const orderRowCardsCondition = viewOption === 'thermalComfort' ? effectiveHasThermalComfort : effectiveHasHealth;

    return orderRowCardsCondition ? 2 : 1;
  }

  return (
    <ContentContainer>
      <BigNumbers
        hasHealth={effectiveHasHealth}
        hasThermalComfort={effectiveHasThermalComfort}
        loading={effectiveLoading}
      />
      {viewOption === 'thermalComfort' ? (
        <ThermalComfort
          hasThermalComfort={effectiveHasThermalComfort}
          loading={effectiveLoading}
          order={effectiveHasThermalComfort ? 1 : 2}
        />
      ) : (
        <Health
          hasHealth={effectiveHasHealth}
          loading={effectiveLoading}
          order={effectiveHasHealth ? 1 : 2}
        />
      )}
      <RowCardsOverview
        hasWater={effectiveHasWater}
        loading={effectiveLoading}
        hasEnergy={effectiveHasEnergy}
        order={getOrderRowCards()}
      />
    </ContentContainer>
  );
}
